import { AnalyticsFrequency } from '@constants'
import { useBusinessContext, useDateRangeContext } from '@context'
import { dashboardQueryKey, dashboardQueryOptions, fetchSeriesBatch } from '@query'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import {
  FIGURE_ACCOUNT_TYPES,
  FIGURE_KEYS,
  FigureKey,
  useDashboardAccountStats
} from './useDashboardAccountStats'

export const SPARK_MONTH_COUNT = 10

export type KeyFigureKey = FigureKey

export interface KeyFigureSparkPoint {
  date: string
  value: number
}

export interface KeyFigure {
  key: KeyFigureKey
  isYearToDate: boolean
  value: number
  changeRatio: number | null
  spark: KeyFigureSparkPoint[]
  peak: KeyFigureSparkPoint | null
}

const PLACEHOLDER_FIGURES: KeyFigure[] = FIGURE_KEYS.map(key => ({
  key,
  isYearToDate: true,
  value: 0,
  changeRatio: null,
  spark: [],
  peak: null
}))

// `|| 0` collapses the negative zero the expense sign flip produces on an empty
// range, which would otherwise format as "−0,00 €".
const centsToEur = (cents: number | undefined) => (cents ?? 0) / 100 || 0

export const useKeyFigures = ({ enabled = true }: { enabled?: boolean } = {}): {
  figures: KeyFigure[]
  isLoading: boolean
} => {
  const { slug } = useBusinessContext()
  const { selectedRange } = useDateRangeContext()
  const {
    data: stats,
    areFiguresPending,
    yearStart,
    isReady
  } = useDashboardAccountStats({
    enabled
  })

  const today = dayjs()
  const sparkTo = today.format('YYYY-MM-DD')
  const sparkFrom = today
    .startOf('month')
    .subtract(SPARK_MONTH_COUNT - 1, 'month')
    .format('YYYY-MM-DD')

  const rangeFrom = selectedRange?.date_from
  const rangeTo = selectedRange?.date_to
  const isYearToDate =
    yearStart != null &&
    (!rangeFrom || (rangeFrom === yearStart.format('YYYY-MM-DD') && dayjs(rangeTo).isAfter(today)))

  const sparksEnabled = enabled && Boolean(slug) && isReady

  const sparkQueryKey = dashboardQueryKey(slug, 'figureSparks', sparkFrom, sparkTo)
  const { data: sparks, isPending: areSparksPending } = useQuery({
    queryKey: sparkQueryKey,
    queryFn: () =>
      fetchSeriesBatch({
        slug,
        specs: FIGURE_KEYS.map(key => ({
          key,
          account_type: FIGURE_ACCOUNT_TYPES[key],
          date_from: sparkFrom,
          date_to: sparkTo,
          freq: AnalyticsFrequency.M
        }))
      }),
    ...{ enabled: sparksEnabled, ...dashboardQueryOptions(slug) }
  })

  const isLoading = !isReady || (yearStart != null && (areFiguresPending || areSparksPending))

  return useMemo(() => {
    if (!yearStart) {
      return { figures: isLoading ? PLACEHOLDER_FIGURES : [], isLoading }
    }
    const figures = FIGURE_KEYS.map(key => {
      const current = stats?.[key]
      const previous = stats?.[`${key}__previous`]
      const series = sparks?.[key] ?? []

      const sign = key === 'expenses' ? -1 : 1
      const value = centsToEur(current) * sign || 0
      const previousValue = centsToEur(previous) * sign || 0

      const spark = series
        .slice(-SPARK_MONTH_COUNT)
        .map(entry => ({ date: entry.label, value: centsToEur(entry.value) * sign || 0 }))

      const peak = spark.reduce<KeyFigureSparkPoint | null>(
        (best, point) => (best === null || point.value > best.value ? point : best),
        null
      )

      return {
        key,
        isYearToDate,
        value,
        changeRatio: previousValue === 0 ? null : (value - previousValue) / Math.abs(previousValue),
        spark,
        peak
      }
    })

    return { figures, isLoading }
  }, [sparks, stats, isLoading, isYearToDate, yearStart])
}
